import { GRAVIDADE_M_S2 } from '../dominio/tabletop/estatica'
import type { VetorTabletop } from '../dominio/tabletop/tipos'
import type { Rapier } from './motor'

/**
 * Ancoragem em V simulada: uma massa pendurada em duas fixações por cordas
 * elásticas que só puxam (nunca empurram).
 *
 * O motor cuida do corpo rígido, da gravidade e da integração; a corda é uma
 * força elástica `T = k·(comprimento − repouso)` aplicada a cada passo, o que
 * deixa a tensão legível sem depender das forças internas do motor.
 *
 * Dois modos, que são o ponto didático:
 *  - `fixa`: cada perna é uma corda própria, com o comprimento que tinha no
 *    desenho. O ponto central quase não se move e a carga se divide pela
 *    geometria (a perna mais vertical segura mais).
 *  - `equalizada`: uma corda só passa pelas duas fixações e leva o anel da
 *    carga, sem atrito. O anel desliza até o ponto mais baixo e a tensão fica
 *    igual nas duas pernas.
 *
 * Só V: no W e no M não há uma corda única que equalize três ou quatro pernas,
 * então eles ficam só no cálculo (`calcularAncoragem`).
 */

export type ModoSimulacaoV = 'fixa' | 'equalizada'

export interface EntradaSimulacaoV {
  readonly fixacaoA: VetorTabletop
  readonly fixacaoB: VetorTabletop
  /** Onde o anel está no desenho; a corda tem este comprimento de repouso. */
  readonly centro: VetorTabletop
  readonly massaKg: number
  readonly modo: ModoSimulacaoV
  /** Uma fixação que cede (0 = A, 1 = B). Só no modo `fixa`. */
  readonly cedeIndice?: 0 | 1
  /** Quando a fixação cede, em segundos. */
  readonly instanteCedeS?: number
  readonly duracaoS?: number
}

export interface QuadroSimulacaoV {
  readonly t: number
  readonly centro: VetorTabletop
  /** Tensão em cada perna, em N; 0 numa perna solta ou que cedeu. */
  readonly tensoesN: readonly [number, number]
}

export interface ResultadoSimulacaoV {
  readonly quadros: readonly QuadroSimulacaoV[]
  readonly assentado: QuadroSimulacaoV
  /** Maior tensão em qualquer perna durante todo o movimento, em N. */
  readonly picoTensaoN: number
  /** A massa terminou (quase) parada. */
  readonly assentou: boolean
  /** Rigidez usada nas cordas, em N/m: o cálculo analítico precisa do mesmo valor para comparar. */
  readonly rigidezNPorM: number
}

const PASSO_S = 1 / 240
const QUADROS_POR_SEGUNDO = 30
const DURACAO_PADRAO_S = 20
/** Amortecimento do movimento como um todo (balanço da massa), por segundo. */
const AMORTECIMENTO = 1.5
/** Razão de amortecimento da própria corda: sem ela uma corda rígida que afrouxa e estica a cada passo bombeia energia e a massa nunca para. */
const AMORTECIMENTO_DA_CORDA = 0.7
/** Velocidade abaixo da qual a massa conta como parada, em m/s. */
const VELOCIDADE_DE_REPOUSO = 5e-3

/** Rigidez das cordas: esticam cerca de 1/400 do comprimento de um metro por peso da carga, então o assentamento é de milímetros. */
export const rigidezDaCorda = (massaKg: number) => 400 * massaKg * GRAVIDADE_M_S2

const dist = (a: VetorTabletop, b: VetorTabletop) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])

export function simularAncoragemV(R: Rapier, entrada: EntradaSimulacaoV): ResultadoSimulacaoV {
  const { fixacaoA, fixacaoB, centro, massaKg, modo } = entrada
  const duracao = entrada.duracaoS ?? DURACAO_PADRAO_S
  const k = rigidezDaCorda(massaKg)
  const c = 2 * AMORTECIMENTO_DA_CORDA * Math.sqrt(k * massaKg)
  const fixacoes = [fixacaoA, fixacaoB] as const

  const mundo = new R.World({ x: 0, y: -GRAVIDADE_M_S2, z: 0 })
  mundo.timestep = PASSO_S
  const corpo = mundo.createRigidBody(
    R.RigidBodyDesc.dynamic().setTranslation(centro[0], centro[1], centro[2]).setLinearDamping(AMORTECIMENTO),
  )
  mundo.createCollider(R.ColliderDesc.ball(0.1).setMass(massaKg).setSensor(true), corpo)

  const repouso = [dist(fixacaoA, centro), dist(fixacaoB, centro)] as const
  const repousoTotal = repouso[0] + repouso[1]

  const quadros: QuadroSimulacaoV[] = []
  const passos = Math.round(duracao / PASSO_S)
  const passosPorQuadro = Math.round(1 / PASSO_S / QUADROS_POR_SEGUNDO)
  let pico = 0
  let ultimaVelocidade = Infinity

  for (let i = 0; i <= passos; i += 1) {
    const t = i * PASSO_S
    const p = corpo.translation()
    const posicao: VetorTabletop = [p.x, p.y, p.z]
    const cedeu = (indice: number) => entrada.cedeIndice === indice && t >= (entrada.instanteCedeS ?? 0)
    const comprimentos = [dist(fixacaoA, posicao), dist(fixacaoB, posicao)] as const
    // quanto cada perna se alonga por segundo: a componente da velocidade que se afasta da fixação
    const v = corpo.linvel()
    const alongamento = [0, 1].map((j) => {
      const f = fixacoes[j]!
      const cj = comprimentos[j]!
      return cj < 1e-9 ? 0 : -((f[0] - posicao[0]) * v.x + (f[1] - posicao[1]) * v.y + (f[2] - posicao[2]) * v.z) / cj
    }) as [number, number]

    let tensoes: [number, number]
    if (modo === 'equalizada') {
      const esticada = comprimentos[0] + comprimentos[1] - repousoTotal
      // a corda é uma só: o amortecimento atua no alongamento total
      const T = Math.max(0, k * esticada + c * (alongamento[0] + alongamento[1]))
      tensoes = esticada > 0 ? [T, T] : [0, 0]
    } else {
      tensoes = [0, 1].map((j) => {
        const esticada = comprimentos[j]! - repouso[j]!
        return cedeu(j) || esticada <= 0 ? 0 : Math.max(0, k * esticada + c * alongamento[j]!)
      }) as [number, number]
    }

    corpo.resetForces(true)
    let fx = 0
    let fy = 0
    let fz = 0
    for (let j = 0; j < 2; j += 1) {
      const c = comprimentos[j]!
      if (c < 1e-9) continue
      const f = fixacoes[j]!
      fx += (tensoes[j]! * (f[0] - posicao[0])) / c
      fy += (tensoes[j]! * (f[1] - posicao[1])) / c
      fz += (tensoes[j]! * (f[2] - posicao[2])) / c
    }
    corpo.addForce({ x: fx, y: fy, z: fz }, true)

    pico = Math.max(pico, tensoes[0], tensoes[1])
    if (i % passosPorQuadro === 0 || i === passos) quadros.push({ t, centro: posicao, tensoesN: tensoes })
    if (i < passos) mundo.step()
    const vf = corpo.linvel()
    ultimaVelocidade = Math.hypot(vf.x, vf.y, vf.z)
  }
  mundo.free()

  return {
    quadros,
    assentado: quadros[quadros.length - 1]!,
    picoTensaoN: pico,
    assentou: ultimaVelocidade < VELOCIDADE_DE_REPOUSO,
    rigidezNPorM: k,
  }
}
